import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { useDispatch } from "react-redux";

import {
  fetchPriceSummary,
} from "../../actions/priceActions";

import {
  useCart,
} from "../../components/CartContext";

import "./css/CheckoutScreen.css";


function CheckoutScreen() {

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    restaurantId,
    tableId,
  } = useParams();


  /* =====================================================
     CART
  ===================================================== */

  const {
    cart,
    cartCount,
    cartTotal,
  } = useCart();


  /* =====================================================
     PRICE SUMMARY
  ===================================================== */

  const [
    priceSummary,
    setPriceSummary,
  ] = useState({

    subtotal: 0,

    tax: 0,

    serviceCharge: 0,

    total: 0,

  });


  /* =====================================================
     STATE
  ===================================================== */

  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  /* =====================================================
     HOME PATH
  ===================================================== */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =====================================================
     FORMAT PRICE
  ===================================================== */

  const formatPrice = (value) => {

    return Number(
      value || 0
    ).toFixed(2);

  };


  /* =====================================================
     FETCH PRICE SUMMARY
  ===================================================== */

  useEffect(() => {

    const calculatePrice = async () => {

      if (
        !restaurantId ||
        !cart ||
        cart.length === 0
      ) {

        return;

      }


      try {

        setLoading(true);

        setError("");


        /* ---------------------------------------------
           SEND ONLY ITEM ID + QUANTITY
        --------------------------------------------- */

        const items =
          cart.map(
            (item) => ({

              menu_item_id:
                item.id,

              quantity:
                item.quantity,

            })
          );


        /* ---------------------------------------------
           BACKEND PRICE CALCULATION
        --------------------------------------------- */

        const response =
          await dispatch(
            fetchPriceSummary(
              restaurantId,
              items
            )
          );


        /* ---------------------------------------------
           SAVE BACKEND TOTAL
        --------------------------------------------- */

        setPriceSummary({

          subtotal:
            Number(
              response?.subtotal || 0
            ),

          tax:
            Number(
              response?.tax || 0
            ),

          serviceCharge:
            Number(
              response?.service_charge || 0
            ),

          total:
            Number(
              response?.total || 0
            ),

        });


      } catch (err) {

        console.error(
          "Price summary error:",
          err
        );


        setError(
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to calculate your order total."
        );


      } finally {

        setLoading(false);

      }

    };


    calculatePrice();


  }, [
    dispatch,
    restaurantId,
    cart,
  ]);


  /* =====================================================
     BACK TO CART
  ===================================================== */

  const handleBackToCart = () => {

    navigate(
      `${homePath}/cart`
    );

  };


  /* =====================================================
     CONTINUE TO ORDER
  ===================================================== */

  const handleContinue = () => {

    /* ---------------------------------------------
       EMPTY CART
    --------------------------------------------- */

    if (
      !cart ||
      cart.length === 0
    ) {

      navigate(homePath);

      return;

    }


    /* ---------------------------------------------
       GO TO ORDER SCREEN
    --------------------------------------------- */

    navigate(
      `${homePath}/order`,
      {
        state: {

          subtotal:
            Number(
              priceSummary.subtotal
            ),

          tax:
            Number(
              priceSummary.tax
            ),

          serviceCharge:
            Number(
              priceSummary.serviceCharge
            ),

          total:
            Number(
              priceSummary.total
            ),

        },
      }
    );

  };


  /* =====================================================
     EMPTY CART
  ===================================================== */

  if (
    !cart ||
    cart.length === 0
  ) {

    return (

      <main className="theme8-checkout-page">

        <div className="theme8-checkout-empty">

          <div className="theme8-checkout-empty-icon">
            +
          </div>


          <span className="theme8-checkout-label">
            CHECKOUT
          </span>


          <h1>
            Your cart is empty
          </h1>


          <p>
            Add some delicious dishes
            before continuing.
          </p>


          <button
            type="button"
            className="theme8-checkout-primary-button"
            onClick={() =>
              navigate(homePath)
            }
          >

            Explore Menu

            <span>
              →
            </span>

          </button>

        </div>

      </main>

    );

  }


  /* =====================================================
     MAIN
  ===================================================== */

  return (

    <main className="theme8-checkout-page">


      {/* =================================================
          HEADER
      ================================================= */}

      <section className="theme8-checkout-header">

        <div className="theme8-checkout-container">


          <button
            type="button"
            className="theme8-checkout-back"
            onClick={handleBackToCart}
          >

            <span>
              ←
            </span>

            Back to Cart

          </button>


          <div className="theme8-checkout-title-row">


            <div>

              <span className="theme8-checkout-label">
                ALMOST THERE
              </span>


              <h1>
                Checkout
              </h1>

            </div>


            <div className="theme8-checkout-table">

              <span>
                TABLE
              </span>


              <strong>
                {tableId}
              </strong>

            </div>


          </div>

        </div>

      </section>


      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <section className="theme8-checkout-content">

        <div className="theme8-checkout-container">

          <div className="theme8-checkout-layout">


            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div className="theme8-checkout-order">


              {/* =================================================
                  ORDER ITEMS
              ================================================= */}

              <div className="theme8-checkout-section-head">

                <div>

                  <span>
                    01
                  </span>

                  <h2>
                    Review your order
                  </h2>

                </div>

              </div>


              <div className="theme8-checkout-items">

                {cart.map(
                  (item) => (

                    <article
                      className="theme8-checkout-item"
                      key={item.id}
                    >


                      {/* IMAGE */}

                      <div className="theme8-checkout-item-image">

                        {item.image ? (

                          <img
                            src={item.image}
                            alt={item.name}
                          />

                        ) : (

                          <div className="theme8-checkout-placeholder">
                            ✦
                          </div>

                        )}

                      </div>


                      {/* INFO */}

                      <div className="theme8-checkout-item-info">

                        <span>

                          {item.categoryName ||
                            "INDIAN CUISINE"}

                        </span>


                        <h3>
                          {item.name}
                        </h3>


                        <p>

                          ₹
                          {formatPrice(
                            item.price
                          )}

                        </p>

                      </div>


                      {/* QUANTITY */}

                      <div className="theme8-checkout-item-quantity">

                        <span>
                          QTY
                        </span>

                        <strong>
                          {item.quantity}
                        </strong>

                      </div>


                      {/* TOTAL */}

                      <div className="theme8-checkout-item-total">

                        ₹
                        {formatPrice(
                          Number(item.price) *
                          Number(item.quantity)
                        )}

                      </div>

                    </article>

                  )
                )}

              </div>


              {/* =================================================
                  TABLE INFORMATION
              ================================================= */}

              <div className="theme8-table-card">

                <div className="theme8-table-icon">
                  {tableId}
                </div>


                <div>

                  <span>
                    DINING TABLE
                  </span>


                  <h3>
                    Table {tableId}
                  </h3>


                  <p>
                    Your order will be
                    prepared for this table.
                  </p>

                </div>

              </div>


              {/* =================================================
                  INFORMATION
              ================================================= */}

              <div className="theme8-checkout-info">

                <span>
                  ✦
                </span>


                <p>
                  Please review your items
                  and order total before
                  continuing.
                </p>

              </div>

            </div>


            {/* =================================================
                RIGHT SUMMARY
            ================================================= */}

            <aside className="theme8-checkout-summary">


              <div className="theme8-summary-top">

                <span>
                  02
                </span>


                <span>
                  ORDER TOTAL
                </span>

              </div>


              <h2>
                Your order
              </h2>


              {/* =================================================
                  ITEM COUNT
              ================================================= */}

              <div className="theme8-checkout-count">

                <span>

                  {cartCount}

                  {" "}

                  {cartCount === 1
                    ? "item"
                    : "items"}

                </span>


                <strong>

                  ₹
                  {formatPrice(
                    cartTotal
                  )}

                </strong>

              </div>


              <div className="theme8-checkout-divider">
              </div>


              {/* =================================================
                  SUBTOTAL
              ================================================= */}

              <div className="theme8-checkout-price-row">

                <span>
                  Subtotal
                </span>


                <strong>

                  ₹
                  {formatPrice(
                    priceSummary.subtotal
                  )}

                </strong>

              </div>


              {/* =================================================
                  TAX
              ================================================= */}

              <div className="theme8-checkout-price-row">

                <span>
                  Tax
                </span>


                <strong>

                  ₹
                  {formatPrice(
                    priceSummary.tax
                  )}

                </strong>

              </div>


              {/* =================================================
                  SERVICE CHARGE
              ================================================= */}

              <div className="theme8-checkout-price-row">

                <span>
                  Service Charge
                </span>


                <strong>

                  ₹
                  {formatPrice(
                    priceSummary.serviceCharge
                  )}

                </strong>

              </div>


              <div className="theme8-checkout-divider">
              </div>


              {/* =================================================
                  TOTAL
              ================================================= */}

              <div className="theme8-checkout-total">

                <span>
                  TOTAL
                </span>


                <strong>

                  ₹
                  {formatPrice(
                    priceSummary.total
                  )}

                </strong>

              </div>


              {/* =================================================
                  ERROR
              ================================================= */}

              {error && (

                <div className="theme8-checkout-error">

                  <strong>
                    Unable to calculate total
                  </strong>


                  <p>
                    {error}
                  </p>

                </div>

              )}


              {/* =================================================
                  CONTINUE
              ================================================= */}

              <button
                type="button"
                className="theme8-checkout-continue"
                onClick={handleContinue}
                disabled={
                  loading ||
                  Boolean(error)
                }
              >

                {loading ? (

                  <>
                    Calculating...
                  </>

                ) : (

                  <>
                    Continue to Order

                    <span>
                      →
                    </span>
                  </>

                )}

              </button>


              {/* =================================================
                  PAYMENT NOTE
              ================================================= */}

              <div className="theme8-checkout-secure">

                <span>
                  🔒
                </span>


                <p>
                  Payment method will be
                  selected on the next step.
                </p>

              </div>

            </aside>

          </div>

        </div>

      </section>


      {/* =================================================
          FOOTER NOTE
      ================================================= */}

      <section className="theme8-checkout-footer-note">

        <div className="theme8-checkout-container">

          <span>
            ✦
          </span>


          <p>
            Authentic Indian flavours,
            freshly prepared for your table.
          </p>

        </div>

      </section>


    </main>

  );

}


export default CheckoutScreen;